/**
 * passkeyChallenge — mints the nonce a ceremony answers.
 *
 * One action serves both ceremonies, and `ceremony` is the whole difference:
 * `enroll` refuses an unauthenticated caller and stamps that caller's own uid
 * on the stored challenge, `signIn` takes no auth and stamps `null`. Each
 * verify door compares that one stamp. The sign-in arm sends no mail and takes
 * no address, so it opens no enumeration oracle.
 *
 * Fields justified:
 *   ceremony  — which door the minted challenge may be spent at. A closed set
 *               rather than a boolean: a third ceremony would be a third value.
 * Output:
 *   challenge — 32 CSPRNG bytes, unpadded base64url; the authenticator signs
 *               it back inside `clientDataJSON`, and the server never accepts
 *               it as a selector — it hashes what came back.
 * Excluded:
 *   challengeId — the doc id is `sha256(challenge)`, derived from the response
 *               the browser wrote rather than from anything the caller chooses.
 *   rpId / timeout / the user's own id — the client already holds each, and the
 *               server derives the relying-party id from the browser's origin.
 *   expiresAt — the server's window, compared when the challenge is spent.
 */
import { z } from 'zod';

export const PasskeyChallengeInput = z
  .object({
    ceremony: z
      .enum(['enroll', 'signIn'])
      .describe('whether the challenge is for enrolling a new credential or for signing in'),
  })
  .strict();
export type PasskeyChallengeInput = z.infer<typeof PasskeyChallengeInput>;

export const PasskeyChallengeOutput = z
  .object({
    challenge: z
      .string()
      .min(1)
      .describe(
        'the single-use nonce the authenticator signs, as unpadded base64url of 32 random bytes',
      ),
  })
  .strict();
export type PasskeyChallengeOutput = z.infer<typeof PasskeyChallengeOutput>;
